import globalConfig from './config';
import { isExternalSdk } from './environment-utils';
import { isDisabledOn, isEnabledOn } from './features-enabling-utils';

type EnabledFeatures = { [featureName in FormatFeatures]: boolean };

// WARN: anything added to this list HAS TO be strongly tested
const getFeatures = (): EnabledFeatures => ({
  installIcon: isEnabledOn({
    android: { lowerThan: 8 },
    externalSdk: false,
    web: false
  }),

  edgeToEdgeDetection: isEnabledOn({
    ios: false,
    web: false,
    android: { greaterOrEqualTo: 15 },
    androidSdk: { lowerThan: '5.0.0' }
  }),

  installIconAfterBrowser: isEnabledOn({
    android: { greaterOrEqualTo: 8 },
    androidSdk: { greaterOrEqualTo: '3.2.1' },
    externalSdk: false,
    web: false
  }),

  oguryBrowser: isDisabledOn({ externalSdk: true, web: true }),

  recommendedLinks: isEnabledOn({
    ios: true,
    android: { greaterOrEqualTo: 5 },
    externalSdk: false,
    web: false
  }),

  recommendedLinksScriptInjection: isEnabledOn({
    ios: false,
    android: { greaterOrEqualTo: 5 },
    externalSdk: false,
    web: false
  }),

  contentDiscovery: isEnabledOn({
    ios: true,
    android: { greaterOrEqualTo: 5 },
    externalSdk: false,
    web: false
  }),

  contentDiscoveryScriptInjection: isEnabledOn({
    ios: false,
    android: { greaterOrEqualTo: 5 },
    externalSdk: false,
    web: false
  }),

  androidIntents: isEnabledOn({
    android: true,
    ios: false,
    androidSdk: { greaterOrEqualTo: '1.8.0' },
    externalSdk: false,
    web: false
  }),

  iosDefaultBrowser: isEnabledOn({
    ios: true,
    externalSdk: false,
    web: false
  }),

  rageQuitEvent: isDisabledOn({ externalSdk: true, web: true }),

  impressionFormatTrack: isDisabledOn({
    externalSdk: true,
    web: false
  }),

  webviewFormatOguryPrecaching: isEnabledOn({
    android: true,
    androidSdk: { greaterOrEqualTo: '3.0.0' },
    ios: true,
    externalSdk: false,
    web: false
  }),

  // video format specific
  videoOverlayThumbnail: isEnabledOn({
    androidSdk: { greaterOrEqualTo: '3.3.0' },
    ios: true,
    externalSdk: false,
    web: true
  }),

  videoForceUseCustomClose:
    isEnabledOn({
      androidSdk: { lowerThan: '3.0.0' },
      web: true
    }) ||
    // allow in external for android and ios even if ios is disable in internal
    isExternalSdk(),

  videoFormatLegacyPlaybackError: isEnabledOn({
    androidSdk: { lowerThan: '3.0.0' },
    web: false
  }),

  videoSendInternalTracks: isDisabledOn({
    externalSdk: true,
    web: false
  }),

  videoReward: isEnabledOn({
    android: true,
    androidSdk: { greaterOrEqualTo: '2.2.0' },
    ios: true,
    externalSdk: false,
    web: false
  }),

  clearCloseCallbackAfterClick: isEnabledOn({
    android: true,
    androidSdk: { greaterOrEqualTo: '3.0.0' },
    ios: true,
    externalSdk: false,
    web: false
  }),

  injectMraidOnWebview: isEnabledOn({
    android: true,
    ios: true,
    iosSdk: { lowerThan: '2.5.0' },
    externalSdk: true,
    web: true
  }),

  skan: isEnabledOn({
    android: false,
    ios: true,
    iosSdk: { greaterOrEqualTo: '3.4.0' },
    externalSdk: false,
    web: false
  }),

  sdkiOSKeepAlive: isEnabledOn({
    android: false,
    ios: true,
    externalSdk: false,
    web: false
  })
});

const featuresCache: EnabledFeatures = getFeatures();

export function isFeatureEnable(
  featureName: FormatFeatures,
  formatConfig?: FormatConfigType<unknown>
) {
  const features: EnabledFeatures =
    globalConfig.NODE_ENV === 'test' ? getFeatures() : featuresCache;
  if (typeof features[featureName] === 'undefined') {
    throw new Error(`Feature unknown: ${featureName}`);
  }

  const override = formatConfig?.featureEnablingOverride;

  if (override && override.hasOwnProperty(featureName)) {
    return override[featureName];
  }

  return features[featureName];
}
